import React, { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { apiFetch } from '../lib/api';
import { reasonList, SPAM_PILL } from '../lib/spamReasons'; // @@FORM_SPAM_v96@@
import { APP_STATUSES, APP_LABELS, appPill, LINK, fmt } from '../lib/hiring';

// @@JOB_RESUME_v113@@
const fmtSize = (n) => (n < 1048576 ? Math.max(1, Math.round(n / 1024)) + ' KB' : (n / 1048576).toFixed(1) + ' MB');

// @@JOBS_v86@@ One applicant: their answers, contact, status, and a notes log.
// Opening this page marks the application read (server-side).
export default function HiringApplicant() {
  const { id } = useParams();
  const [a, setA] = useState(null);
  const [msg, setMsg] = useState('');
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);

  // @@JOB_RESUME_v113@@ authenticated download (a plain <a href> would skip the Bearer header)
  async function downloadResume() {
    setMsg('');
    const res = await apiFetch('/api/jobs/applications/' + id + '/resume');
    if (!res.ok) { const d = await res.json().catch(() => ({})); setMsg(d.error || 'Could not download the resume.'); return; }
    const m = /filename="([^"]+)"/.exec(res.headers.get('Content-Disposition') || '');
    const url = URL.createObjectURL(await res.blob());
    const link = document.createElement('a');
    link.href = url; link.download = m ? m[1] : 'resume';
    document.body.appendChild(link); link.click(); link.remove();
    URL.revokeObjectURL(url);
  }

  async function load() {
    const res = await apiFetch('/api/jobs/applications/' + id);
    const d = await res.json().catch(() => ({}));
    if (res.ok) setA(d.application); else setMsg(res.status === 404 ? 'Applicant not found.' : (d.error || 'Could not load.'));
  }
  useEffect(() => { load(); }, [id]);

  // @@FORM_SPAM_v96@@
  async function setSpam(spam) {
    const res = await apiFetch('/api/jobs/applications/' + id + '/spam', {
      method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ spam }),
    });
    const d = await res.json().catch(() => ({}));
    if (res.ok) setA(d.application); else setMsg(d.error || 'Could not update.');
  }
  async function setStatus(status) {
    const res = await apiFetch('/api/jobs/applications/' + id, {
      method: 'PATCH', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ status }),
    });
    const d = await res.json().catch(() => ({}));
    if (res.ok) setA(d.application); else setMsg(d.error || 'Could not update the status.');
  }
  async function addNote() {
    if (!note.trim()) return;
    setBusy(true);
    try {
      const res = await apiFetch('/api/jobs/applications/' + id + '/notes', {
        method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ body: note }),
      });
      const d = await res.json().catch(() => ({}));
      if (res.ok) { setA(d.application); setNote(''); } else setMsg(d.error || 'Could not add the note.');
    } finally { setBusy(false); }
  }

  if (!a) {
    return <div className="hq-page-content"><div className="hq-page-head"><h1>Applicant</h1><p>{msg || 'Loading…'}</p></div>
      <Link to="/hiring" style={LINK}>← Back to Hiring</Link></div>;
  }

  const first = (a.name || '').split(' ')[0];
  const mailto = `mailto:${encodeURIComponent(a.email)}?subject=${encodeURIComponent(`Your application — ${a.posting_title}`)}&body=${encodeURIComponent(`Hi ${first},\n\n`)}`;

  return (
    <div className="hq-page-content">
      <Link to="/hiring" style={{ ...LINK, fontSize: 13 }}>← Hiring</Link>
      <div className="hq-page-head" style={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'space-between', gap: 16, marginTop: 8 }}>
        <div>
          <h1>{a.name} <span style={{ ...appPill(a.status), verticalAlign: 'middle', marginLeft: 8 }}>{APP_LABELS[a.status]}</span></h1>
          <p>{a.posting_title} · applied {fmt(a.created_at)}</p>
        </div>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
          <label style={{ display: 'flex', gap: 8, alignItems: 'center', fontSize: 13, color: 'var(--hq-muted)' }}>Status
            <select className="hq-select" value={a.status} onChange={(e) => setStatus(e.target.value)}>
              {APP_STATUSES.map((s) => <option key={s} value={s}>{APP_LABELS[s]}</option>)}
            </select>
          </label>
          <a className="hq-btn" href={mailto} style={{ display: 'inline-flex', alignItems: 'center', textDecoration: 'none' }}>Email {first}</a>
          <button type="button" className="hq-signout" style={{ width: 'auto', padding: '0 14px', marginTop: 0, color: a.spam ? '#31b44b' : '#ff8c86' }}
            onClick={() => setSpam(!a.spam)}>{a.spam ? 'Not spam' : 'Mark as spam'}</button>
        </div>
      </div>
      {msg && <p className="hq-msg">{msg}</p>}
      {(a.spam || (a.suspect && a.suspect.length > 0)) && (
        <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap', margin: '0 0 16px', padding: '10px 14px', borderRadius: 10, fontSize: 13, lineHeight: 1.5, color: 'var(--hq-txt)', background: 'rgba(255,122,115,.07)', border: '1px solid rgba(255,122,115,.22)' }}>
          <span style={SPAM_PILL}>{a.spam ? 'Spam' : 'Looks like spam'}</span>
          <span>{a.spam ? 'Quarantined by the spam filter — no email went out for it. If it is a real person, click Not spam.' : 'The spam filter would flag this one.'} <span style={{ color: '#ff8c86' }}>{reasonList(a.spam ? a.spam_reasons : a.suspect)}</span></span>
        </div>
      )}

      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 20, alignItems: 'flex-start' }}>
        <div className="hq-panel" style={{ flex: '2 1 480px', minWidth: 0 }}>
          <div className="hq-panel-head"><h2>Their answers</h2></div>
          <div style={{ padding: '4px 20px 12px' }}>
            {a.answers.map((x, i) => (
              <div key={i} style={{ padding: '16px 0', borderBottom: i < a.answers.length - 1 ? '1px solid var(--hq-line)' : 0 }}>
                <div style={{ display: 'flex', gap: 10, alignItems: 'center', fontSize: 13, color: 'var(--hq-muted)', fontWeight: 600 }}>
                  <span style={{ width: 22, height: 22, borderRadius: '50%', background: '#31b44b', color: '#0a0a0b', fontWeight: 700, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', fontSize: 12, flexShrink: 0 }}>{i + 1}</span>
                  {x.q}
                </div>
                <p style={{ margin: '10px 0 0', whiteSpace: 'pre-wrap', lineHeight: 1.6, fontSize: 15 }}>{x.a}</p>
              </div>
            ))}
            {a.answers.length === 0 && <p className="hq-empty">No answers.</p>}
          </div>
        </div>

        <div className="hq-panel" style={{ flex: '1 1 280px', minWidth: 0 }}>
          <div className="hq-panel-head"><h2>Contact</h2></div>
          <table className="hq-table"><tbody>
            <tr><td className="hq-role">Email</td><td><a href={`mailto:${a.email}`} style={LINK}>{a.email}</a></td></tr>
            <tr><td className="hq-role">Phone</td><td>{a.phone ? <a href={`tel:${a.phone.replace(/[^0-9+]/g, '')}`} style={LINK}>{a.phone}</a> : '—'}</td></tr>
            <tr><td className="hq-role">Based in</td><td>{a.city || '—'}</td></tr>
            <tr><td className="hq-role">Resume</td><td>{a.resume
              ? <><button type="button" className="hq-btn" onClick={downloadResume}>Download</button>
                <div style={{ color: 'var(--hq-muted)', fontSize: 12, marginTop: 6, wordBreak: 'break-all' }}>{a.resume.filename} · {fmtSize(a.resume.size_bytes)}</div></>
              : '—'}</td></tr>
            <tr><td className="hq-role">Link</td><td style={{ wordBreak: 'break-all' }}>{a.link ? <a href={a.link} target="_blank" rel="noopener noreferrer nofollow" style={LINK}>{a.link.replace(/^https?:\/\//, '')}</a> : '—'}</td></tr>
          </tbody></table>
        </div>
      </div>

      <div className="hq-panel" style={{ marginTop: 20 }}>
        <div className="hq-panel-head"><h2>Notes &amp; history</h2></div>
        <div style={{ padding: '16px 20px 0' }}>
          <textarea className="hq-input" rows={3} style={{ width: '100%', boxSizing: 'border-box', height: 'auto', padding: '10px 12px' }}
            placeholder="Add a note (call notes, interview feedback…)" aria-label="New note" value={note} maxLength={5000} onChange={(e) => setNote(e.target.value)} />
          <div style={{ display: 'flex', justifyContent: 'flex-end', margin: '10px 0' }}>
            <button className="hq-btn" type="button" onClick={addNote} disabled={busy || !note.trim()}>{busy ? 'Adding…' : 'Add note'}</button>
          </div>
        </div>
        <table className="hq-table"><tbody>
          {a.notes.map((n) => (
            <tr key={n.id}>
              <td style={{ whiteSpace: 'pre-wrap' }}>
                {n.to_status && n.from_status !== null
                  ? <span style={{ color: 'var(--hq-muted)' }}>Status: {APP_LABELS[n.from_status] || n.from_status} → <strong style={{ color: 'var(--hq-txt)' }}>{APP_LABELS[n.to_status] || n.to_status}</strong></span>
                  : n.body}
              </td>
              <td style={{ color: 'var(--hq-muted)', fontSize: 12, whiteSpace: 'nowrap', textAlign: 'right', verticalAlign: 'top' }}>
                {fmt(n.created_at)}{n.author ? ` · ${n.author}` : ''}
              </td>
            </tr>
          ))}
          <tr><td style={{ color: 'var(--hq-muted)' }}>Applied via xyzmerch.com/jobs</td>
            <td style={{ color: 'var(--hq-muted)', fontSize: 12, whiteSpace: 'nowrap', textAlign: 'right' }}>{fmt(a.created_at)}</td></tr>
        </tbody></table>
      </div>
    </div>
  );
}
